/**
 * Production Issues Page
 *
 * Coordinator view for managing all production issues.
 *
 * Layout mirrors the Production Settings Console: a left side nav
 * (`IssuesRail`, sticky on xl+, wrapping onto rows above the pane on narrower
 * screens) listing one entry per issue type, with the active type's pane
 * beside it. Picking a type scrolls the page back to the top.
 * Which issue types appear, their order, labels, icons and counts all come
 * from `../issueTabs`.
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Settings, SlidersHorizontal } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useLocation as useLocationContext } from '@/contexts/LocationContext';
import { useIssueCounts } from '../hooks/useProductionIssues';
import { OrderDetailModal } from './OrderDetailModal';
import { IssuesErrorBoundary } from './IssuesErrorBoundary';
import { OOSIssuesList } from './OOSIssuesList';
import { RedoIssuesList } from './RedoIssuesList';
import { StuckBinsList } from './StuckBinsList';
import { CancelledItemsList } from './CancelledItemsList';
import { OnHoldList } from './OnHoldList';
import { ShippingIssuesTab } from './ShippingIssuesTab';
import { PrintFileIssuesList } from './PrintFileIssuesList';
import { SplitOrderIssuesList } from './SplitOrderIssuesList';
import { OrderChangeIssuesList } from './OrderChangeIssuesList';
import { CatalogIssuesList } from './CatalogIssuesList';
import { IssuesRail } from './IssuesRail';
import { useShippingFailures } from '@/features/packing-station/hooks/useShipping';
import { usePortlessBlockedCount } from '../hooks/usePortlessBlocked';
import { useOrgRoleSurfaces } from '@/hooks/useOrgRoleSurfaces';
import { getScrollContainer } from '@/lib/scroll-container';
import { useRole } from '@/hooks/use-role';
import { useProductionSettings } from '@/features/production-settings/hooks/useProductionSettings';
import {
  ALL_ISSUE_TAB_VALUES,
  computeRenderedIssueTabs,
  issueTabBadgeCount,
  resolveEffectiveIssueTab,
  applyClientTabVisibility,
} from '../issueTabs';
import type { TabValue, ProductionIssue } from '../types';

/**
 * Issues Page Content (wrapped by error boundary)
 */
function IssuesPageContent() {
  const { t } = useTranslation('production');
  const navigate = useNavigate();
  const { selectedLocation } = useLocationContext();
  // `?tab=` deep-link support (initial value only) — the resolver flow returns
  // to `/production/issues?tab=split-orders` after a successful apply.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<TabValue>(
    ALL_ISSUE_TAB_VALUES.includes(tabParam as TabValue) ? (tabParam as TabValue) : 'oos'
  );
  // `?tab=oos&search=` seeds the OOS list's search box (initial value only):
  // the Order Queue's OOS badge links here with the order's name. Dropped from
  // the URL once the pane has read it (below), so a search the user cleared
  // does not come back when the pane remounts on a tab switch.
  const oosSearchParam = tabParam === 'oos' ? searchParams.get('search') ?? undefined : undefined;

  // Per-user show/hide preference for the issue tabs (localStorage-backed).
  const { settings } = useProductionSettings();
  const hiddenIssueTabs = settings.hiddenIssueTabs;

  // A pure client additionally never sees the floor-only tabs. Applied AFTER
  // computeRenderedIssueTabs rather than folded into `hiddenIssueTabs`, because
  // that helper deliberately REVEALS a hidden tab when it is the `?tab=`
  // deep-link target — which would let `?tab=redos` undo this. `isPureClient`
  // is false until the surfaces query resolves, so this never flash-hides.
  const { isPureClient } = useOrgRoleSurfaces();
  // CX never sees the floor-action tabs (redos/stuck-bins/cancelled) — same
  // noise-filter mechanism as the pure-client set. Order changes IS shown to CX.
  const { isCX } = useRole();

  // Tabs to render. A tab that is the current `?tab=` deep-link target is always
  // revealed for this session so resolver/alert return paths (e.g.
  // `?tab=split-orders`) never land on an empty pane, even if the user hid it.
  const tabsToRender = useMemo(
    () => {
      return applyClientTabVisibility(
        computeRenderedIssueTabs(hiddenIssueTabs, tabParam),
        isPureClient,
        isCX(),
      );
    },
    [hiddenIssueTabs, tabParam, isPureClient, isCX]
  );

  // If the active tab is not currently rendered (hidden without being the
  // deep-link target), fall back to the first rendered tab.
  const effectiveTab = resolveEffectiveIssueTab(tabsToRender, activeTab);

  useEffect(() => {
    if (effectiveTab !== activeTab) {
      setActiveTab(effectiveTab);
    }
  }, [effectiveTab, activeTab]);

  // Not before the pane mounts: the location can load after the first render.
  const oosSearchSeeded = oosSearchParam !== undefined && !!selectedLocation?.id && effectiveTab === 'oos';
  useEffect(() => {
    if (!oosSearchSeeded) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('search');
        return next;
      },
      { replace: true }
    );
  }, [oosSearchSeeded, setSearchParams]);

  // Rail pick: switch the pane and scroll the page to the top (as the Settings
  // Console does).
  const handleSelect = useCallback((value: TabValue) => {
    setActiveTab(value);
    getScrollContainer(null)?.scrollTo({ top: 0 });
  }, []);

  // State for Order Detail Modal
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [selectedIssue, setSelectedIssue] = useState<ProductionIssue | null>(null);

  // Handler to open Order Detail Modal
  const handleViewDetails = useCallback(
    (issueId: string, issue?: ProductionIssue) => {
      setSelectedIssueId(issueId);
      setSelectedIssue(issue ?? null);
    },
    []
  );

  // Handler to close Order Detail Modal
  const handleCloseModal = useCallback(() => {
    setSelectedIssueId(null);
    setSelectedIssue(null);
  }, []);

  const { data: counts, isLoading: countsLoading } = useIssueCounts(
    selectedLocation?.id ?? ''
  );

  const { data: shippingData } = useShippingFailures(selectedLocation?.id ?? null);
  const shippingUnresolved = shippingData?.unresolvedCount ?? 0;
  // BLU-732: the Shipping tab badge counts BOTH shipping states — failures at
  // the ship step + Portless-blocked orders (shared cache with the tab's list).
  const portlessBlocked = usePortlessBlockedCount(selectedLocation?.id ?? null);

  if (!selectedLocation?.id) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <AlertTriangle
              className="h-12 w-12 text-muted-foreground"
              aria-hidden="true"
            />
            <p className="mt-4 text-lg font-medium">{t('issues.noLocationSelected')}</p>
            <p className="text-sm text-muted-foreground">
              {t('issues.selectLocationPrompt')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Badge count per tab — shared accessor in ../issueTabs so these badges and
  // the aggregate totals (sidebar nav badge, Dashboard alert) can never drift.
  const badgeCountFor = (value: TabValue): number =>
    issueTabBadgeCount(value, counts, shippingUnresolved, portlessBlocked);

  const isVisible = (value: TabValue) => tabsToRender.some((tab) => tab.value === value);

  const locationId = selectedLocation.id;
  const renderPane = (value: TabValue) => {
    switch (value) {
      case 'oos':
        return (
          <OOSIssuesList
            locationId={locationId}
            onViewDetails={handleViewDetails}
            initialSearch={oosSearchParam}
          />
        );
      case 'split-orders':
        // BLU-563 R3: gear entry to Settings -> Production -> Split Orders,
        // rendered via the list's headerAction slot so it sits right-aligned
        // in the heading row in normal flow (no absolute overlay). Split-
        // orders pane only, not the global header.
        return (
          <SplitOrderIssuesList
            locationId={locationId}
            headerAction={
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                    aria-label={t('issues.splitOrders.settingsAria')}
                    onClick={() => navigate('/production/settings#split-orders')}
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  {t('issues.splitOrders.settingsTooltip')}
                </TooltipContent>
              </Tooltip>
            }
          />
        );
      case 'redos':
        return <RedoIssuesList locationId={locationId} />;
      case 'stuck-bins':
        return <StuckBinsList locationId={locationId} />;
      case 'cancelled':
        return <CancelledItemsList locationId={locationId} />;
      case 'on-hold':
        return <OnHoldList locationId={locationId} />;
      case 'shipping':
        // BLU-732: sub-tabs, Portless-blocked orders (BLU-713, the state
        // BEFORE a shipping failure) + ship-step failures, so neither buries
        // the other. Collapses to just failures when healthy.
        return <ShippingIssuesTab locationId={locationId} />;
      case 'print-files':
        return <PrintFileIssuesList locationId={locationId} />;
      case 'order-changes':
        return <OrderChangeIssuesList locationId={locationId} />;
      case 'catalog':
        return <CatalogIssuesList locationId={locationId} />;
    }
  };

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8 space-y-6 animate-enter">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-destructive/10">
            <AlertTriangle
              className="h-6 w-6 text-destructive"
              aria-hidden="true"
            />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">{t('issues.title')}</h1>
            <p className="text-muted-foreground">
              {selectedLocation.name} - {t('issues.subtitle')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* OOS "orders affected" header badge — follows the OOS tab's
              visibility so hiding the tab hides its number here too. */}
          {isVisible('oos') &&
            (countsLoading ? (
              <Skeleton className="h-6 w-32" />
            ) : counts && counts.oos_order_count > 0 ? (
              <Badge variant="destructive" className="text-sm">
                {t('issues.ordersAffected', { count: counts.oos_order_count })}
              </Badge>
            ) : null)}
          {/* Configure which tabs are visible on this page (per-user). Deep-links
              to the "Show or hide issue tabs" section in Production Settings. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                aria-label={t('issues.tabVisibility.gearAria')}
                onClick={() => navigate('/production/settings#issue-tabs')}
              >
                <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              {t('issues.tabVisibility.gearTooltip')}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* ARIA live region for issue updates */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {counts &&
          t('issues.ariaLiveSummary', { oosCount: counts.oos_order_count, redoCount: counts.redo_pending, stuckCount: counts.stuck_bins })}
      </div>

      {/* Side nav + active pane, laid out like the Production Settings
          Console: rail on the left (sticky on xl+, wrapping rows above the
          pane on narrower screens), the active issue type's pane beside it.
          xl, not lg: at 1024px with the app sidebar open the pane would be
          ~430px and the issue cards overrun. */}
      <div className="flex flex-col gap-5 xl:flex-row xl:gap-8">
        <div className="xl:sticky xl:top-[calc(var(--top-header-height,3.5rem)+1rem)] xl:self-start xl:max-h-[calc(100dvh-var(--top-header-height,3.5rem)-2rem)] xl:overflow-y-auto">
          <IssuesRail
            tabs={tabsToRender}
            activeTab={effectiveTab}
            onSelect={handleSelect}
            badgeFor={badgeCountFor}
          />
        </div>

        <div className="min-w-0 flex-1">
          {/* Only the active pane is mounted (as the old TabsContent did). */}
          {isVisible(effectiveTab) && renderPane(effectiveTab)}
        </div>
      </div>

      {/* Order Detail Modal */}
      <OrderDetailModal
        issueId={selectedIssueId}
        issue={selectedIssue}
        open={selectedIssueId !== null}
        onClose={handleCloseModal}
      />
    </div>
  );
}

/**
 * Issues Page with Error Boundary
 *
 * Wraps the content in an error boundary for graceful error handling.
 */
export function IssuesPage() {
  return (
    <IssuesErrorBoundary>
      <IssuesPageContent />
    </IssuesErrorBoundary>
  );
}
